import { Box } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import _React from 'react';
import styled from 'styled-components/macro';

import Button from 'components/Button/Button';

export const TicketsPageStyled = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const MessageWrapStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2.5rem 0;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      margin: 6.25rem 0;
    }
  `}
`;
export const FilterBtnStyled = styled(Button)`
  align-self: flex-start;
  margin: 1rem;
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      margin-top: -3rem;
    }
  `}
`;

export const HeaderBoxStyled = muiStyled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 15,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  padding: 0,
  margin: 0,
  [theme.breakpoints.down('md')]: {
    position: 'relative',
  },
}));

/** Wrapper for the collapsible cards row – expand/collapse vertically. */
export const CardsRowSectionStyled = styled.div<{ $expanded: boolean }>`
  overflow: visible;
  margin: ${({ $expanded }): string => ($expanded ? '12px 0 0' : '0')};
  padding: ${({ $expanded }): string => ($expanded ? '0 8px 8px' : '0')};
  background-color: ${({ theme }): string => theme.colors.backgroundDefault};
  transition: margin 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  /* Tablet and mobile: compact spacing, stacked layout */
  @media screen and (max-width: 768px) {
    margin: ${({ $expanded }): string => ($expanded ? '4px 0 0' : '0')};
    padding: ${({ $expanded }): string => ($expanded ? '0 4px 4px' : '0')};
  }
  @media screen and (max-width: 424px) {
    margin: ${({ $expanded }): string => ($expanded ? '4px 0 0' : '0')};
    padding: ${({ $expanded }): string => ($expanded ? '0 4px 4px' : '0')};
  }
`;

/** Collapsible content area – max-height transition; overflow hidden to contain card content. */
export const CardsRowCollapsibleStyled = styled.div<{ $expanded: boolean }>`
  overflow: ${({ $expanded }): string => ($expanded ? 'visible' : 'hidden')};
  max-height: ${({ $expanded }): string => ($expanded ? 'none' : '0')};
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

/**
 * Wrapper for cards layout. Desktop: Message | Dealer+Report in a row.
 * Tablet/mobile: Message stacked above Dealer+Report (Message outside the large row).
 */
export const CardsRowLayoutWrapperStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: nowrap;
  width: 100%;
  min-width: 0;
  padding: 6px 0;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    flex-wrap: wrap;
    gap: 4px;
  }
  @media screen and (max-width: 424px) {
    flex-direction: column;
    gap: 4px;
  }
`;

/** Cards row: gap: 8px is the ONLY source of spacing between Dealer and right column. */
export const BannerAndCardsRowStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: nowrap;
  flex: 0 0 auto;
  min-width: 0;
  padding: 0;
  margin: 0;
  background-color: ${({ theme }): string => theme.colors.backgroundDefault};
  overflow: visible;

  /* Tablet and mobile: full width, stack vertically with compact gap */
  @media screen and (max-width: 768px) {
    flex: 1 1 auto;
    width: 100%;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 4px;
  }

  @media screen and (max-width: 424px) {
    flex: 1 1 auto;
    width: 100%;
    flex-direction: column;
    gap: 4px;
  }
`;

/** DealerCard wrapper: sizes to content; no margin/max-width – spacing from parent gap. */
export const DealerCardWrapStyled = styled.div<{ $isSchedule?: boolean }>`
  flex: 0 0 auto;
  min-width: ${({ $isSchedule }): string => ($isSchedule ? '440px' : '460px')};
  width: fit-content;
  display: flex;
  flex-direction: column;

  /* Tablet and mobile: shrink to fit viewport */
  @media screen and (max-width: 768px) {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }

  @media screen and (max-width: 424px) {
    width: 100%;
    max-width: 100%;
  }
`;

/** Column: Dealer Report / Schedule cards; shrink-to-fit so Banner can expand; no margin – spacing from parent gap. */
export const BannerDealerReportColumnStyled = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 auto;
  min-width: 280px;
  overflow: visible;

  /* Tablet and mobile: full width, compact gap */
  @media screen and (max-width: 768px) {
    min-width: 0;
    flex: 1 1 auto;
    width: 100%;
    max-width: 100%;
    gap: 4px;
  }

  @media screen and (max-width: 424px) {
    width: 100%;
    max-width: 100%;
    gap: 4px;
  }
`;

/** Row under the banner: Dealer Report card (left), Schedule card (right). */
export const BannerCardsRowStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
  flex-wrap: nowrap;
  width: 100%;
  margin: 0;
  padding: 0;
  overflow: visible;

  /* Tablet and mobile: stack vertically, compact gap */
  @media screen and (max-width: 768px) {
    flex-direction: column;
    flex-wrap: wrap;
    gap: 4px;
  }

  @media screen and (max-width: 424px) {
    flex-direction: column;
    gap: 4px;
  }
`;

/** Column wrapper for BannerMessage + DealerCard stacked vertically */
export const BannerDealerColumnStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
`;

/**
 * Shared wrapper for Dealer and Schedule report cards.
 * - Height: 230px
 * - Width: fit-content (content determines width)
 */
export const ReportCardWrapStyled = styled.div`
  height: 230px;
  min-height: 230px;
  max-height: 230px;
  width: fit-content;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex-shrink: 0;
  overflow: visible;

  /* InfoCard fills wrapper so all cards share same padding (compact) and height */
  & > div {
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* Tablet and mobile: shrink to fit viewport; flexible height for collapsible */
  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 0;
    max-height: none;
  }
  @media screen and (max-width: 424px) {
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 0;
    max-height: none;
  }
`;

/** Dealer Billing card wrapper: same height as Dealer card (230px). */
export const DealerReportCardWrapStyled = styled.div`
  height: 230px;
  min-height: 230px;
  max-height: 230px;
  width: fit-content;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex-shrink: 0;
  overflow: visible;

  & > div {
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* Tablet and mobile: widen to fit viewport */
  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
  @media screen and (max-width: 424px) {
    width: 100%;
    max-width: 100%;
  }
`;

/** Schedule card when compact: same height as Dealer card (230px). */
export const ScheduleCardCompactWrapStyled = styled.div`
  height: 230px;
  min-height: 230px;
  max-height: 230px;
  width: fit-content;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex-shrink: 0;
  overflow: visible;

  & > div {
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* Tablet and mobile: fill viewport width; flexible height for collapsible */
  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 0;
    max-height: none;
  }
  @media screen and (max-width: 424px) {
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 0;
    max-height: none;
  }
`;

/** Wrapper so DealerReport aligns in row and keeps fit-content width; same gap as banner to dealer card. */
export const DealerReportRowWrapStyled = styled.div`
  align-self: stretch;
  margin: 0;
  width: fit-content;
  flex-shrink: 0;
  overflow: visible;

  /* Tablet and mobile: full width for Billing card */
  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
  @media screen and (max-width: 424px) {
    width: 100%;
    max-width: 100%;
  }
`;

/** Wrapper for list/table content below the header. Top padding matches header padding above search bar.
 * overflow: hidden so scroll happens inside TicketsTable (like WarrQueue) – enables sticky headers. */
export const TableContentWrapStyled = styled.div`
  margin-top: 0;
  padding: 12px 4px 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }): string => theme.colors.backgroundDefault};
`;

export const ContentWrapStyled = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: visible;
  transition: 0.3s linear;

  /* Tablet and mobile: allow vertical scroll */
  @media screen and (max-width: 768px) {
    overflow-y: auto;
  }
  @media screen and (max-width: 424px) {
    overflow-y: auto;
  }
`;
